import { Set, OrderedMap } from 'immutable';
import Collection from '@repo/utils/models/Collection';
import Note, { NoteType } from '@repo/utils/models/Note';
import Contact from '@repo/utils/models/Contact';
import Occasion from '@repo/utils/helpers/Occasion';
import type { ID, ReduxAction } from '@repo/utils/types';
import AppState from '../app-state';

export interface NotesState {
  collection: Collection<Note>;
  indexViewCollection: Collection<ID>;
  localCache: Collection<Note>;
  currentlyEditing: Note | null;
  newNoteType: NoteType;
  pendingRequests: {
    delete: Set<ID>;
    update: Set<ID>;
  };
}

export const initialNotesState = {
  collection: new Collection<Note>({ limit: 50 }),
  indexViewCollection: new Collection<ID>({ limit: 50 }),
  localCache: new Collection<Note>(),
  currentlyEditing: null,
  newNoteType: { color: null, icon: null, label: null },
  pendingRequests: {
    delete: Set<ID>(),
    update: Set<ID>(),
  },
};

export const selectNotes = (state: AppState): Collection<Note> => {
  const allNotes = state.data.notes.collection;
  return state.data.notes.indexViewCollection.copyWith({
    items: state.data.notes.indexViewCollection.items.mapEntries(([id, _]) => {
      return [id, allNotes.items.get(id)];
    }),
  });
};

export const selectNewNoteType = (state: AppState): NoteType => state.data.notes.newNoteType;
export const selectNoteType =
  (noteID: ID) =>
  (state: AppState): NoteType => {
    const note = state.data.notes.collection.items.get(noteID);
    return note?.type || { color: null, icon: null, label: null };
  };

export function notesReducer(
  state: NotesState = initialNotesState,
  { type, payload }: ReduxAction,
): NotesState {
  const originalRequestID = payload && payload.originalRequestID;
  let contactResponseNotes = null;
  let _collection = state.collection;
  let _currentlyEditing = state.currentlyEditing;
  let _indexViewCollection = state.indexViewCollection;

  switch (type) {
    case 'FETCH_ALL_NOTES':
      return {
        ...state,
        indexViewCollection: state.indexViewCollection.copyWith({ isFetching: true }),
      };

    case 'FETCH_ALL_NOTES_SUCCEEDED':
      const responseCollection = payload.response.results.map((note: any) => [
        note.id.toString(),
        new Note(note),
      ]);

      const indexViewItems = state.indexViewCollection.items.merge(
        responseCollection.map(([id, _]) => [id, id]),
      );

      _collection = state.collection.copyWith({
        items: state.collection.items.merge(responseCollection),
      });

      _indexViewCollection = state.indexViewCollection.copyWith({
        isFetching: false,
        updated: new Occasion(),
        offset: indexViewItems.count(),
        persistedItemsOnServer: payload.response.count,
        items: indexViewItems,
      });

      return {
        ...state,
        collection: _collection,
        indexViewCollection: _indexViewCollection,
      };

    case 'FETCH_ALL_NOTES_FAILED':
      return {
        ...state,
        collection: state.indexViewCollection.copyWith({
          isFetching: false,
          error: payload,
        }),
      };

    case 'FETCH_NOTE_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.set(
            String(payload.response?.id),
            new Note(payload.response),
          ),
        }),
      };

    // TODO: this will be stale once we release timeline
    case 'FETCH_ALL_CONTACT_NOTES_SUCCEEDED':
      if (!payload.response?.results?.length) {
        return state;
      }

      contactResponseNotes = payload.response.results || [];

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.merge(
            contactResponseNotes.map((note: any) => [
              note.id.toString(),
              new Note({
                ...note,
                contact: new Contact({ id: payload.contactID }),
              }),
            ]),
          ),
        }),
      };

    case 'FETCH_CONTACT_TIMELINE_SUCCEEDED':
    case 'FETCH_CONTACT_TIMELINE_WINDOW_SUCCEEDED':
    case 'FETCH_FILTERED_CONTACT_TIMELINE_SUCCEEDED':
      if (!payload.response?.results?.length) {
        return state;
      }

      if (payload.filterType && !['notes', 'reminders'].includes(payload.filterType)) {
        return state;
      }

      contactResponseNotes = (payload.response.results || []).filter(
        ({ item_type }) => item_type === 'note',
      );

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.merge(
            contactResponseNotes.map((note: any) => {
              const id = note.id.split(':')[1];
              return [
                id,
                new Note({
                  id,
                  content: note.content,
                  created: note.date,
                  updated: note.metadata.updated,
                  reminder: note.metadata.reminder,
                  contact: new Contact({ id: payload.contactID }),
                  type: {
                    color: note.metadata.color,
                    icon: note.metadata.icon,
                    label: note.metadata.label,
                  },
                  metadata: note.metadata,
                }),
              ];
            }),
          ),
        }),
      };

    // We load notes in from our reminders in order to be able to take edit actions
    // on these notes if they aren't already loaded from the notes index endpoint
    case 'FETCH_ALL_UPCOMING_REMINDERS_SUCCEEDED':
      const reminderNotes = payload.response.results.reduce(
        (allNotes, { contact, date, id, note }) => {
          if (note?.id) {
            const reminderNote = new Note({ ...note, contact, reminder: { id, date } });
            return allNotes.concat([[String(note.id), reminderNote]]);
          }
          return allNotes;
        },
        [],
      );

      // if reminder notes are not yet loaded, merge them in
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.mergeWith(
            (existingNote: Note, _) => existingNote,
            reminderNotes,
          ),
        }),
      };

    case 'DELETE_CONTACT_SUCCEEDED':
      const deletedContactNotes = state.collection.items.filter(
        (note, _) => note.contact.id == payload.id,
      );

      return {
        ...state,
        collection: state.collection.copyWith({
          // remove the deleted contact's notes from the store
          items: state.collection.items.filter((note, _) => note.contact.id != payload.id),
        }),
        indexViewCollection: state.indexViewCollection.copyWith({
          // remove the deleted contact's notes from the index view
          items: state.indexViewCollection.items.filter((_, id) => !deletedContactNotes.has(id)),
        }),
      };

    case 'SET_CURRENTLY_EDITING_NOTE':
      _collection = state.collection;

      // if we were editing a new note but did not persist any changes, delete that note from the store
      if (
        state.currentlyEditing &&
        !state.currentlyEditing.isPersisted &&
        state.currentlyEditing.content.length == 0
      ) {
        _collection = state.collection.copyWith({
          items: state.collection.items.delete(state.currentlyEditing.id),
        });
        // if we have a currentlyEditing note in the store, make sure that changes there are merged into notes when we reset
      } else if (state.currentlyEditing && state.collection.items.has(state.currentlyEditing.id)) {
        _collection = state.collection.copyWith({
          items: state.collection.items.set(
            state.currentlyEditing.id.toString(),
            state.currentlyEditing,
          ),
        });
        // if the edited note is not included in the store, we can assume it is new. place it at the front of the ordered notes
      } else if (state.currentlyEditing && !state.collection.items.has(state.currentlyEditing.id)) {
        _collection = state.collection.copyWith({
          items: state.collection.items.set(
            state.currentlyEditing.id.toString(),
            state.currentlyEditing,
          ),
        });
        _indexViewCollection = state.indexViewCollection.copyWith({
          items: OrderedMap([
            [state.currentlyEditing.id.toString(), state.currentlyEditing.id],
          ]).concat(state.indexViewCollection.items),
        });
      }

      return {
        ...state,
        collection: _collection,
        indexViewCollection: _indexViewCollection,
        currentlyEditing: payload ? new Note(payload) : null,
      };

    case 'UPDATE_NEW_NOTE_TYPE':
      if (state.currentlyEditing?.isPersisted) {
        return {
          ...state,
          newNoteType: payload,
          currentlyEditing: state.currentlyEditing.copyWith({ type: payload }),
        };
      } else {
        return {
          ...state,
          newNoteType: payload,
        };
      }

    case 'SET_CREATING_NEW_NOTE_IN_CONTACT_VIEW':
      if (payload.creatingNewNote) {
        return state;
      } else {
        return {
          ...state,
          newNoteType: { color: null, icon: null, label: null },
        };
      }

    case 'UPDATE_NOTE_TYPE':
      const note = state.collection.items.get(String(payload.noteID));
      if (!note) {
        return state;
      }

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(String(payload.noteID), (note) =>
            note?.copyWith({ type: payload.noteType }),
          ),
        }),
      };

    case 'UPDATE_CURRENTLY_EDITING_NOTE':
      return {
        ...state,
        currentlyEditing: payload,
      };

    case 'UPDATE_CURRENTLY_EDITING_NOTE_REQUESTED':
    case 'CREATE_CURRENTLY_EDITING_NOTE_REQUESTED':
      return {
        ...state,
        currentlyEditing: state.currentlyEditing
          ? state.currentlyEditing.copyWith({ isFetching: true })
          : null,
      };

    case 'UPDATE_CURRENTLY_EDITING_NOTE_SUCCEEDED':
      // We may have merged an updating note back into the collection, or it may still be the note that
      // is actively being edited. We handle the first case on the collection and the second case on `currentlyEditing`.
      return {
        ...state,
        collection:
          state.currentlyEditing?.id == String(payload.noteID)
            ? state.collection
            : state.collection.copyWith({
                items: state.collection.items.update(String(payload.noteID), (note) =>
                  note?.copyWith({ isFetching: false }),
                ),
              }),
        currentlyEditing:
          state.currentlyEditing?.id == String(payload.noteID)
            ? state.currentlyEditing.copyWith({
                isFetching: false,
                updated: new Occasion(),
              })
            : null,
      };

    case 'CREATE_CURRENTLY_EDITING_NOTE_SUCCEEDED':
      // If the original note that was created has already been merged into the main notes store, we should be sure to update its ID there so
      // it doesn't accidentally get re-created on edit. Otherwise, update the currently editing note with the persisted ID.
      _collection = state.collection;
      _currentlyEditing = state.currentlyEditing;

      if (state.collection.items.has(originalRequestID)) {
        _collection = state.collection.copyWith({
          items: state.collection.items.mapEntries(([k, v]) => {
            if (k == originalRequestID) {
              return [
                payload.response.id.toString(),
                v.copyWith({
                  id: payload.response.id,
                  isFetching: false,
                  updated: new Occasion(),
                  type: payload.response.type,
                }),
              ];
            }
            return [k, v];
          }),
        });
      }

      if (state.indexViewCollection.items.has(originalRequestID)) {
        _indexViewCollection = state.indexViewCollection.copyWith({
          items: state.indexViewCollection.items.mapEntries(([k, v]) => {
            if (k == originalRequestID) {
              return [payload.response.id.toString(), payload.response.id.toString()];
            }
            return [k, v];
          }),
        });
      }

      if (state.currentlyEditing && state.currentlyEditing.id == originalRequestID) {
        _currentlyEditing = state.currentlyEditing.copyWith({
          id: payload.response.id,
          isFetching: false,
          updated: new Occasion(),
          type: payload.response.type,
        });
      }

      return {
        ...state,
        collection: _collection,
        currentlyEditing: _currentlyEditing,
        indexViewCollection: _indexViewCollection,
        // ensure that we have the persisted ID to use for resolving any pending requests
        pendingRequests: {
          update: state.pendingRequests.update
            .map((id) => (id == originalRequestID ? payload.response.id : id).toString())
            .toSet(),
          delete: state.pendingRequests.delete
            .map((id) => (id == originalRequestID ? payload.response.id : id).toString())
            .toSet(),
        },
      };

    case 'UPDATE_CURRENTLY_EDITING_NOTE_FAILED':
    case 'CREATE_CURRENTLY_EDITING_NOTE_FAILED':
      return {
        ...state,
        currentlyEditing: state.currentlyEditing
          ? state.currentlyEditing.copyWith({ isFetching: false, error: payload })
          : null,
      };

    case 'UPDATE_NOTE_DELAYED':
      return {
        ...state,
        pendingRequests: {
          ...state.pendingRequests,
          update: state.pendingRequests.update.add(payload),
        },
      };
    case 'DELETE_NOTE_DELAYED':
      return {
        ...state,
        pendingRequests: {
          update: state.pendingRequests.update.delete(payload),
          delete: state.pendingRequests.delete.add(payload),
        },
      };

    case 'CREATE_NOTE':
      _collection = state.collection.copyWith({
        items: state.collection.items.set(
          payload.id.toString(),
          new Note({ ...payload, isFetching: true }),
        ),
      });

      // insert new note at front of the existing list of notes
      _indexViewCollection = state.indexViewCollection.copyWith({
        items: OrderedMap([[payload.id.toString(), payload.id]]).concat(
          state.indexViewCollection.items,
        ),
      });

      return {
        ...state,
        collection: _collection,
        indexViewCollection: _indexViewCollection,
      };

    case 'CREATE_NOTE_SUCCEEDED':
      if (!state.collection.items.has(originalRequestID)) {
        return {
          ...state,
          collection: state.collection.copyWith({
            items: state.collection.items.set(
              String(payload.response.id),
              new Note(payload.response),
            ),
          }),
        };
      }

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.mapEntries(([noteID, note]) => {
            if (noteID == originalRequestID) {
              return [
                payload.response.id.toString(),
                note.copyWith({
                  id: payload.response.id,
                  isFetching: false,
                  updated: new Occasion(),
                }),
              ];
            }
            return [noteID, note];
          }),
        }),
        indexViewCollection: state.indexViewCollection.copyWith({
          items: state.indexViewCollection.items.mapEntries(([id, noteID]) => {
            if (id == originalRequestID) {
              return [payload.response.id.toString(), payload.response.id.toString()];
            }
            return [id, noteID];
          }),
        }),
      };

    case 'CREATE_NOTE_FAILED':
      // This action is dispatched when a note created in the ContactAndNoteModal fails to save.
      // We add the note to the store on the initial request, then pull it out here if it failed.
      // Likewise, we handle this action type in the modal state reducer by restoring the note such
      // that it is displayed the next time the modal is opened.
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(originalRequestID, (note) =>
            note?.copyWith({ isFetching: false, error: true }),
          ),
        }),
        indexViewCollection: state.indexViewCollection.copyWith({
          items: state.indexViewCollection.items.delete(originalRequestID),
        }),
      };

    case 'UPDATE_NOTE_REQUESTED':
      if (payload.isReminderEditorUpdate && _currentlyEditing?.id == payload.id) {
        if (payload.reminder?.date) {
          _currentlyEditing = _currentlyEditing.updateReminder(payload.reminder.date);
        } else {
          _currentlyEditing = _currentlyEditing.removeReminder();
        }
      }

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(String(payload.id), (note) =>
            note?.copyWith({ ...payload, isFetching: true, error: false }),
          ),
        }),
        currentlyEditing: _currentlyEditing,
      };

    case 'UPDATE_NOTE_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(String(payload.noteID), (note) =>
            note?.copyWith({ isFetching: false, updated: new Occasion() }),
          ),
        }),
      };

    case 'UPDATE_NOTE_FAILED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(String(payload.noteID), (note) =>
            note?.copyWith({ isFetching: false, error: true }),
          ),
        }),
      };

    case 'DELETE_NOTE':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.delete(payload.id.toString()),
        }),
        indexViewCollection: state.indexViewCollection.copyWith({
          items: state.indexViewCollection.items.delete(payload.id.toString()),
        }),
        currentlyEditing:
          state.currentlyEditing && state.currentlyEditing.id == payload.id.toString()
            ? null
            : state.currentlyEditing,
      };
    case 'DELETE_NOTE_SUCCEEDED':
    // nothing to do
    case 'DELETE_NOTE_FAILED':
    // alert error -- this note was not successfully deleted -- this should be handled by appMessaging middleware. revert to initial state?
    default:
      return state;
  }
}
